import React, {useEffect, useState} from "react";
import {OptionType} from "../../model/option-type/OptionType";
import {
    Control,
    Controller,
    FieldArrayWithId,
    useFieldArray,
    UseFormResetField,
    UseFormSetValue,
    useWatch
} from "react-hook-form";
import ReactModal from "react-modal";
import {
    ControlledPaginateAsyncSelectForTable
} from "../ui/form-components/controlled/ControlledPaginateAsyncSelectForTable";
import {TablePage} from "../../model/utils/TablePage";
import {NomenclatureGuideRow} from "../../model/nomenclature/NomenclatureGuideRow";
import {PiPencilLight, PiTrashLight} from "react-icons/pi";
import {ControlledInputFieldForTable} from "../ui/form-components/controlled/ControlledInputFieldForTable";
import {AddButtonText} from "../ui/buttons/AddButtonText";
import {FiX} from "react-icons/fi";
import {MaterialOrProductCreateForm} from "./MaterialOrProductCreateForm";
import {NomenclatureFormEdit} from "../nomenclature/form/NomenclatureFormEdit";
import {NomenclatureForm} from "../../model/nomenclature/NomenclatureForm";
import {
    useLazyGetNomenclatureQuery,
    useLazySearchNomenclatureGuidePageQuery
} from "../../store/backend-api/backend.api";
import {DeleteButtonText} from "../ui/buttons/DeleteButtonText";

interface FormProps {
    control: Control<FormFields>,
    setValue: UseFormSetValue<any>,
    resetField: UseFormResetField<any>,
    deletedMaterialsIds: number[],
    setDeletedMaterialsIds: (ids: number[]) => void,
    editFromModal?: boolean,
    stepId?: number
}

interface FormFields {
    materials: {
        materialId: number | null,
        materialName: OptionType<number> | null,
        materialQuantity: string,
        materialStep: number | null,
        targetBillOfMaterialsId: OptionType<number> | null
    }[]
}

export function InternalMaterialsForm(props: FormProps) {
    const [doSearchNomenclatureGuidePage] = useLazySearchNomenclatureGuidePageQuery()
    const [doGetNomenclature, {data: nomenclatureForEdit, isSuccess: isSuccessGetNomenclature}] = useLazyGetNomenclatureQuery()
    const [modalForNomenclatureEditIsOpen, setModalForNomenclatureEditIsOpen] = useState<boolean>(false)
    const [modalForNomenclatureCreateIsOpen, setModalForNomenclatureCreateIsOpen] = useState<boolean>(false)
    const [createdNomenclatureOption, setCreatedNomenclatureOption] = useState<OptionType<number> | null>(null)
    const [selectedMaterialFieldIndex, setSelectedMaterialFieldIndex] = useState<number | null>(null)
    const [editedNomenclature, setEditedNomenclature] = useState<null | NomenclatureForm>(null)

    const control: Control<FormFields> = props.control
    const currentFormValue = useWatch({control})

    ReactModal.defaultStyles = {
        overlay: {
            ...ReactModal.defaultStyles.overlay,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.5)'
        },
        content: {
            border: '1px solid #ccc',
            background: '#fff',
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            borderRadius: '4px',
            outline: 'none',
            padding: '20px',
            maxHeight: "90%",
            overflowX: 'auto',
            overflowY: 'auto'
        }
    }

    const {fields, append, remove, update} = useFieldArray({
        name: "materials",
        control,
        rules: {}
    })

    function onClickTrashIcon(field: FieldArrayWithId<FormFields, "materials", "id">, index: number) {
        if (field.materialId !== null) {
            props.setDeletedMaterialsIds([...props.deletedMaterialsIds, field.materialId!])
        }
        remove(index)
    }

    function rowMapper() {
        return (
            <tbody>
            {fields.map((field, index) =>
                <tr key={field.id}>
                    {props.editFromModal && props.stepId && <>
                        <td className="border border-black text-center align-bottom">
                            <Controller control={control} name={`materials.${index}.materialStep`} rules={{}}
                                        render={({field: {value, onChange}}) =>
                                            <input className="w-5 h-5 " type="checkbox" value={props.stepId} onChange={(event) =>
                                                onChange(event.target.checked ? event.target.value : null)}
                                                   checked={value !== null}></input>
                                        }/>
                        </td>
                        <td className="border border-black px-2 text-sm font-light">{field.materialId}</td></>}
                    <td className="border border-black"><ControlledPaginateAsyncSelectForTable control={control}
                                                                                               rules={{
                                                                                                   required: {
                                                                                                       value: true,
                                                                                                       message: "Это обязательное поле"
                                                                                                   }
                                                                                               }}
                                                                                               name={`materials.${index}.materialName`}
                                                                                               request={doSearchNomenclatureGuidePage}
                                                                                               mapFunc={(data: TablePage<NomenclatureGuideRow>) =>
                                                                                                   data.rows.map(row =>
                                                                                                       ({
                                                                                                           label: `${row.fullName}, ${row.measurementUnit}`,
                                                                                                           value: row.id
                                                                                                       })
                                                                                                   )}
                                                                                               additionalOptions={[{
                                                                                                   label: "Создать материал",
                                                                                                   value: -1
                                                                                               }]}
                                                                                               isClearable={false}
                                                                                               setModalIsOpen={setModalForNomenclatureCreateIsOpen}
                                                                                               selectedFieldIndex={index}
                                                                                               setSelectedFieldIndex={setSelectedMaterialFieldIndex}/>
                    </td>
                    {currentFormValue.materials?.[index]?.materialName !== null && currentFormValue.materials?.[index]?.materialName !== undefined &&
                        <td title={"Редактировать номенклатуру"} className="border border-black cursor-pointer"
                            onClick={() => {
                                doGetNomenclature(currentFormValue.materials?.[index]?.materialName?.value!)
                            }}>
                            <PiPencilLight className="mt-[2px] h-5 w-5 hover:text-blue-900"/>
                        </td>}
                    {(currentFormValue.materials?.[index]?.materialName === null || currentFormValue.materials?.[index]?.materialName === undefined) &&
                        <td className="border border-black"></td>}
                    <td className="border border-black"><ControlledInputFieldForTable control={control} rules={{
                        pattern: {
                            value: /^[0-9]*[.]?[1-9][0-9]{0,2}$/g,
                            message: 'Введите положительное число с 3 или менее знаками после "."',
                        },
                        validate: {
                            positive: (value: string) => value !== '0' || 'Количество больше нуля'
                        }
                    }}
                                                                                      name={`materials.${index}.materialQuantity`}
                                                                                      additionalStyles={"min-w-[100px] grow"}/>
                    </td>
                    {(props.editFromModal === undefined || !props.editFromModal) &&
                        <td className="border border-black"><ControlledInputFieldForTable control={control} rules={{}}
                                                                                          readonly={true}
                                                                                          name={`materials.${index}.materialStep`}
                                                                                          additionalStyles={"w-[80px]"}/>
                        </td>}
                    <td title={"Удалить материал"} className="border border-black cursor-pointer" onClick={() => {
                        onClickTrashIcon(field, index)
                    }}><PiTrashLight className="mt-[2px] h-5 w-5 hover:text-red-900"/></td>
                </tr>)}
            </tbody>
        )
    }

    function chooseAllMaterials() {
        fields.forEach((field, index) => update(index, {...field, materialStep: props.stepId!}))
    }
    function resetChoiceMaterials() {
        fields.forEach((field, index) => update(index, {...field, materialStep: null}))
    }

    useEffect(() => {
        if (!modalForNomenclatureCreateIsOpen && createdNomenclatureOption !== null && selectedMaterialFieldIndex !== null) {
            props.setValue(`materials.${selectedMaterialFieldIndex}.materialName`, createdNomenclatureOption)
            setSelectedMaterialFieldIndex(null)
            setCreatedNomenclatureOption(null)
        }
        if (!modalForNomenclatureCreateIsOpen && createdNomenclatureOption === null && selectedMaterialFieldIndex !== null) {
            props.resetField(`materials.${selectedMaterialFieldIndex}.materialName`)
            setSelectedMaterialFieldIndex(null)
        }
    }, [modalForNomenclatureCreateIsOpen]);

    useEffect(() => {
        if (isSuccessGetNomenclature) {
            setEditedNomenclature(nomenclatureForEdit!)
            setModalForNomenclatureEditIsOpen(true)
        }
    }, [nomenclatureForEdit]);

    return (
        <>
            {props.editFromModal && <div className="flex flex-row gap-x-4 mb-2">
                <AddButtonText onClick={chooseAllMaterials} name={"Выбрать всё"}/>
                <div className="-mt-[6px]">
                    <DeleteButtonText onClick={resetChoiceMaterials} label={"Сбросить выбор"}/>
                </div>
            </div>}
            <table className="border-collapse mb-3">
                <thead>
                <tr>
                    {props.editFromModal && <>
                        <th className="border border-black px-1 text-sm font-medium">Выбор для передела</th>
                        <th className="border border-black px-1 text-sm font-medium">ID</th></>}
                    <th className="border border-black px-1 text-sm font-medium">Наименование</th>
                    <th className="border border-black px-1 text-sm font-medium"></th>
                    <th className="border border-black px-1 text-sm font-medium">Количество</th>
                    {!props.editFromModal &&
                        <th className="border border-black px-1 text-sm font-medium">Передел</th>}
                    <th className="border border-black px-1 text-sm font-medium"></th>
                </tr>
                </thead>
                {rowMapper()}
            </table>
            <AddButtonText onClick={() => {
                append({
                    materialId: null,
                    materialName: null,
                    materialQuantity: "",
                    materialStep: null,
                    targetBillOfMaterialsId: null
                })
            }} name={"Добавить материал"}/>
            <ReactModal isOpen={modalForNomenclatureCreateIsOpen}
                        onRequestClose={() => setModalForNomenclatureCreateIsOpen(false)}>
                <div className="flex flex-row justify-end">
                    <button type={"button"}
                            title="Закрыть"
                            onClick={() => setModalForNomenclatureCreateIsOpen(false)}
                            className="-mr-1 text-gray-400/[.60] rounded-[5px]">
                        <FiX className="h-8 w-8"/>
                    </button>
                </div>
                <MaterialOrProductCreateForm setCreatedNomenclatureOption={setCreatedNomenclatureOption}
                                             setModalIsOpen={setModalForNomenclatureCreateIsOpen}/>
            </ReactModal>
            {editedNomenclature !== null && <ReactModal isOpen={modalForNomenclatureEditIsOpen}
                                                        onRequestClose={() => {
                                                            setModalForNomenclatureEditIsOpen(false)
                                                            setEditedNomenclature(null)
                                                        }}>
                <div className="flex flex-row justify-end">
                    <button type={"button"}
                            title="Закрыть"
                            onClick={() => {
                                setModalForNomenclatureEditIsOpen(false)
                                setEditedNomenclature(null)
                            }}
                            className="-mr-1 text-gray-400/[.60] rounded-[5px]">
                        <FiX className="h-8 w-8"/>
                    </button>
                </div>
                <NomenclatureFormEdit data={editedNomenclature} editFromBillOfMaterialInterface={true}
                                      onSaveFromBillOnMaterialsUI={setModalForNomenclatureEditIsOpen}/>
            </ReactModal>}
        </>
    )
}